import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, Star } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  SegmentedControl,
  type BadgeTone,
} from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { ApiError } from '@/internationalization/api-client'
import { formatDateTime } from '@/internationalization/format'
import { qk } from '@/internationalization/query-keys'
import { toast, useSessionStore } from '@/stores'
import { NotFoundPage } from '@/features/misc/NotFoundPage'
import { fetchMyAuthorization } from '@/features/authorization/api'
import { FEEDBACK_STATUSES, inboxApi, type FeedbackItem, type FeedbackStatus } from './api'
import { inboxAccessView } from './inboxAccess'

const PAGE_SIZE = 25

type Filter = 'all' | FeedbackStatus

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'resolved', label: 'Resolved' },
]

// The inbox is read by Sabha staff, in English, so the labels are plain
// strings like the other admin pages' (Vocabulary, Tenants), not i18n keys.
const TYPE_LABEL: Record<string, string> = {
  like: 'Something they like',
  improve: 'Needs improvement',
  feature_request: 'Feature request',
  usability: 'Usability issue',
}
const CATEGORY_LABEL: Record<string, string> = {
  recording: 'Recording and joining meetings',
  transcript: 'Transcript and speakers',
  summary: 'Summary and key points',
  action_items: 'Action items',
  search_ask: 'Search and Ask',
  analytics: 'Analytics',
  integrations: 'Integrations',
  settings: 'Settings and roles',
  other: 'Something else',
}
const STATUS_TONE: Record<FeedbackStatus, BadgeTone> = {
  new: 'brand',
  reviewed: 'info',
  resolved: 'success',
}

/**
 * Product feedback from every workspace, for the three people named on the
 * server (`domain.feedback.FEEDBACK_INBOX_EMAILS`) -- not a role, not a
 * permission. The nav item is hidden from everybody else and the API answers
 * them with a 404, so reaching this page implies `canAccessFeedbackInbox`;
 * it is checked again here because hiding controls is a convenience and the
 * boundary is in the API (and in the database's own policies behind it).
 *
 * Kept apart from "Contact support", which is a mailto link: product feedback
 * is a record to be triaged, not a conversation.
 */
export function FeedbackInboxPage() {
  const queryClient = useQueryClient()
  const canAccess = useSessionStore((s) => s.canAccessFeedbackInbox)
  const [filter, setFilter] = useState<Filter>('all')
  const [page, setPage] = useState(0)

  // `canAccess` stays null when RequireAuth's /authorization/me failed, and the
  // store cannot tell that from "still loading". Ask again here, only while it
  // is unknown, so a failure shows an error with a retry instead of a skeleton.
  const accessCheck = useQuery({
    queryKey: qk.authorization.me,
    queryFn: fetchMyAuthorization,
    enabled: canAccess === null,
  })
  // Same default RequireAuth applies when the flag is absent from the response.
  const confirmedAccess = accessCheck.data && (accessCheck.data.canAccessFeedbackInbox ?? false)
  useEffect(() => {
    if (canAccess === null && confirmedAccess !== undefined) {
      useSessionStore.getState().setCanAccessFeedbackInbox(confirmedAccess)
    }
  }, [canAccess, confirmedAccess])

  const params = { status: filter === 'all' ? undefined : filter, limit: PAGE_SIZE, offset: page * PAGE_SIZE }
  const inbox = useQuery({
    queryKey: qk.admin.feedbackInbox(params),
    queryFn: () => inboxApi.list(params),
    enabled: canAccess === true,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) =>
      inboxApi.setStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.admin.feedbackInbox() }),
    onError: (error) =>
      toast.error('Could not update feedback', error instanceof ApiError ? error.message : undefined),
  })

  // A status change can shrink the list under the current page; step back to
  // the last page that still exists rather than stranding the reader on an
  // empty one with no pager.
  const knownTotal = inbox.data?.total
  useEffect(() => {
    if (knownTotal === undefined) return
    const last = Math.max(0, Math.ceil(knownTotal / PAGE_SIZE) - 1)
    if (page > last) setPage(last)
  }, [knownTotal, page])

  // Hooks above, early returns below (Rules of Hooks). `null` is "not yet
  // known", never "denied": it shows a loader, or an error with a retry when
  // /authorization/me failed.
  const view = inboxAccessView(canAccess, { isError: accessCheck.isError && !accessCheck.isFetching })
  if (view === 'denied') return <NotFoundPage />
  if (view === 'checking' || view === 'failed') {
    return (
      <QueryBoundary
        isLoading={view === 'checking'}
        error={accessCheck.error}
        onRetry={() => void accessCheck.refetch()}
      >
        {null}
      </QueryBoundary>
    )
  }

  const total = inbox.data?.total ?? 0
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  return (
    <div>
      <PageHeader
        title="Feedback inbox"
        description="What people in every workspace have told us. Only you three can see this."
      />
      <div className="mb-4">
        <SegmentedControl<Filter>
          label="Filter by status"
          options={FILTERS}
          value={filter}
          onChange={(next) => {
            setFilter(next)
            setPage(0)
          }}
        />
      </div>
      <QueryBoundary
        isLoading={inbox.isPending}
        error={inbox.error}
        onRetry={inbox.refetch}
        isEmpty={inbox.data?.items.length === 0}
        empty={
          <EmptyState
            icon={<MessageSquare />}
            title="No feedback here"
            description={filter === 'all' ? 'Nothing has been submitted yet.' : 'Nothing has this status.'}
          />
        }
      >
        <div className="space-y-3">
          {inbox.data?.items.map((item) => (
            <FeedbackCard
              key={item.id}
              item={item}
              pendingStatus={setStatus.isPending && setStatus.variables?.id === item.id}
              onSetStatus={(status) => setStatus.mutate({ id: item.id, status })}
            />
          ))}
        </div>
        {total > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between text-sm text-fg-muted">
            <span>
              {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}
            </span>
            <span className="flex gap-2">
              <Button size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button size="sm" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </span>
          </div>
        )}
      </QueryBoundary>
    </div>
  )
}

function FeedbackCard({
  item,
  pendingStatus,
  onSetStatus,
}: {
  item: FeedbackItem
  pendingStatus: boolean
  onSetStatus: (status: FeedbackStatus) => void
}) {
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONE[item.status]}>{item.status}</Badge>
          <span className="text-base font-medium text-fg">{TYPE_LABEL[item.type] ?? item.type}</span>
          <span className="text-base text-fg-muted">· {CATEGORY_LABEL[item.category] ?? item.category}</span>
          {item.rating && (
            <span className="inline-flex items-center gap-0.5 text-brand" aria-label={`${item.rating} of 5 stars`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  aria-hidden
                  className={n <= item.rating! ? 'h-3.5 w-3.5 fill-brand' : 'h-3.5 w-3.5 text-fg-subtle'}
                  strokeWidth={1.5}
                />
              ))}
            </span>
          )}
        </div>
        {/* User-written text: rendered as text by React, never as markup. */}
        <p className="whitespace-pre-wrap break-words text-base text-fg">
          {item.comments ?? <span className="text-fg-subtle">No comments</span>}
        </p>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="text-sm text-fg-muted">
            {item.submitterName ?? 'Unknown'}
            {item.submitterEmail && ` <${item.submitterEmail}>`}
            {item.workspaceName && ` · ${item.workspaceName}`}
            {item.pagePath && ` · ${item.pagePath}`}
            {` · ${formatDateTime(item.createdAt)}`}
          </p>
          <div className="flex gap-1.5">
            {FEEDBACK_STATUSES.filter((status) => status !== item.status).map((status) => (
              <Button
                key={status}
                size="sm"
                variant="ghost"
                disabled={pendingStatus}
                onClick={() => onSetStatus(status)}
              >
                Mark {status}
              </Button>
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
